import { Injectable, signal } from '@angular/core';

/**
 * Carries "Your access to ... was removed." to where the person will read it.
 *
 * Two places, never both: inside the app while they still have another org
 * (`AccessNoticeComponent`, mounted by the shell), or on the sign-in card
 * when the removal signed them out (`LoginComponent` takes it once). Held in
 * memory rather than in a query parameter, so no link can put words in the
 * sign-in card.
 */
@Injectable({ providedIn: 'root' })
export class AccessNoticeService {
  private readonly _inApp = signal<string | null>(null);
  private forLogin: string | null = null;

  /** The notice shown inside the app, or null. */
  readonly inApp = this._inApp.asReadonly();

  showInApp(message: string): void {
    this._inApp.set(message);
  }

  dismissInApp(): void {
    this._inApp.set(null);
  }

  /** For the sign-in page the person is about to land on. */
  leaveForLogin(message: string | null): void {
    this._inApp.set(null);
    this.forLogin = message;
  }

  /**
   * Forget a sign-in notice nobody read: its /login navigation was
   * superseded and another session began. It was about the last one.
   */
  dropForLogin(): void {
    this.forLogin = null;
  }

  /** The sign-in page's notice, once: a later visit shows nothing. */
  takeForLogin(): string | null {
    const message = this.forLogin;
    this.forLogin = null;
    return message;
  }
}
